import React, { useState, useEffect } from 'react';
import Layout from '../common/Layout';
import { PlusCircle } from 'lucide-react';
import { useProfiles } from './useProfiles';
import { useNavigate } from 'react-router-dom';
import { interpretScore } from '@/utils/scoring';
import UserProfile from './UserProfile';
import ProfileList from './ProfileList';
import ProfileForm from './ProfileForm';
import AuthSection from './AuthSection';
import AdditionalOptions from './AdditionalOptions';
import EvaluationHistory from './EvaluationHistory';
import LocationSelector from './LocationSelector';
import { getSelectedProfileId, saveSelectedProfileId } from '@/utils/geolocation';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    profiles,
    isAddingProfile,
    setIsAddingProfile,
    newName,
    setNewName,
    newAge,
    setNewAge,
    session,
    loading,
    handleGoogleLogin,
    handleLogout,
    handleAddProfile,
    handleDeleteProfile
  } = useProfiles();
  
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [lastScore, setLastScore] = useState<number | null>(null);
  const [lastCategory, setLastCategory] = useState<string | null>(null);
  
  const { signInWithGoogle } = useAuth();
  
  useEffect(() => {
    const storedScore = localStorage.getItem('lastAnemiaScore');
    if (storedScore) {
      setLastScore(parseInt(storedScore));
      setLastCategory(interpretScore(parseInt(storedScore)));
    }
    
    const savedProfileId = getSelectedProfileId();
    
    if (savedProfileId && profiles.some(p => p.id === savedProfileId)) {
      setSelectedProfileId(savedProfileId);
    } else if (profiles.length > 0 && !selectedProfileId) {
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profiles[0].id);
      
      if (isValidUUID) {
        setSelectedProfileId(profiles[0].id);
        saveSelectedProfileId(profiles[0].id);
      }
    }
  }, [profiles]);

  const startNewEvaluation = () => {
    navigate('/questionnaire');
  };
  
  const handleProfileClick = (id: string) => {
    if (id) {
      setSelectedProfileId(id);
      saveSelectedProfileId(id);
      
      console.log('Perfil seleccionado:', id);
    }
  };
  
  return (
    <Layout title={t('profile.title')} showBackButton={true}>
      <div className="pb-20 animate-fade-in">
        {session && (
          <UserProfile 
            session={session} 
            lastScore={lastScore} 
            lastCategory={lastCategory} 
          />
        )}
        
        {session && selectedProfileId && (
          <div className="card mb-6">
            <h3 className="text-base font-medium text-gray-800 mb-4">{t('profile.location.title')}</h3>
            <LocationSelector 
              profileId={selectedProfileId}
              initialCountryId={profiles.find(p => p.id === selectedProfileId)?.country_id}
              initialRegionId={profiles.find(p => p.id === selectedProfileId)?.region_id}
            />
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-medium text-gray-800">{t('profile.savedProfiles')}</h3>
          {session && (
            <button 
              onClick={() => setIsAddingProfile(true)}
              className="flex items-center text-sm font-medium text-sangre-600 hover:text-sangre-700"
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              {t('profile.addProfile')}
            </button>
          )}
        </div>
        
        {isAddingProfile && session && (
          <ProfileForm 
            newName={newName}
            setNewName={setNewName}
            newAge={newAge}
            setNewAge={setNewAge}
            handleAddProfile={handleAddProfile}
            setIsAddingProfile={setIsAddingProfile}
          />
        )}
        
        <ProfileList 
          profiles={profiles} 
          session={session} 
          handleDeleteProfile={handleDeleteProfile}
          selectedProfileId={selectedProfileId}
          onProfileClick={handleProfileClick} 
        />
        
        {selectedProfileId && (
          <EvaluationHistory profileId={selectedProfileId} />
        )}
        
        <AuthSection 
          session={session} 
          handleGoogleLogin={signInWithGoogle} 
          handleLogout={handleLogout} 
        />
        
        <AdditionalOptions 
          startNewEvaluation={startNewEvaluation} 
          session={session} 
          handleLogout={handleLogout} 
        />
      </div>
    </Layout>
  );
};

export default ProfileScreen;
